'use client';

import { c as _c } from "react/compiler-runtime";
import type { HeroState } from './utils';
import type { LineChartTemplateCard } from '@effektify/engine';
import type React from 'react';
import { LineChart } from '@effektify/components/Charts';
import { useCardState, useLineChartProps, useReportTranslations } from '@effektify/engine';
import EditOutlined from '@mui/icons-material/EditOutlined';
import Button from '@mui/material/Button';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { AnimatePresence } from 'motion/react';
import { useState } from 'react';
import { IconButton } from 'src/components/Atoms/IconButton';
import { FlexColumn, FlexRow } from 'src/components/Atoms/FlexBox';
import MetricPickerDialog from './MetricPickerDialog';
import StateOverlay from './StateOverlay';
import { getChartMetrics, selectChartSeries, toNumber } from './utils';
interface HeroLineChartProps {
  card: LineChartTemplateCard;
  heroState: HeroState;
  onConnect: () => void;
  isDataSettled: boolean;
}
const CHART_HEIGHT = 320;
const ChartArea = styled('div')(() => ({
  position: 'relative',
  width: '100%',
  minHeight: CHART_HEIGHT,
  backgroundColor: 'transparent'
}));
const PlaceholderLine: React.FC = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <svg width="100%" height={CHART_HEIGHT} viewBox="0 0 600 280" preserveAspectRatio="none" aria-hidden={true}><path d="M0 230 C 80 220, 120 180, 200 170 S 320 120, 400 110 S 520 60, 600 40" fill="none" stroke="currentColor" strokeWidth={2} strokeDasharray="6 5" vectorEffect="non-scaling-stroke" /></svg>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
const HeroLineChart: React.FC<HeroLineChartProps> = t0 => {
  const $ = _c(53);
  const {
    card,
    heroState: pageState,
    onConnect,
    isDataSettled
  } = t0;
  const {
    data,
    shouldRender
  } = useCardState(card);
  const {
    chart
  } = useLineChartProps(card, data);
  const {
    translate
  } = useReportTranslations();
  const [selectedId, setSelectedId] = useState();
  const [draftId, setDraftId] = useState("");
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const hasData = chart.seriesData.some(_temp2);
  const heroState = pageState === "normal" && !hasData && !isDataSettled ? "loading" : pageState;
  if (!shouldRender) {
    return null;
  }
  let t1;
  if ($[0] !== chart.seriesData) {
    t1 = getChartMetrics(chart.seriesData);
    $[0] = chart.seriesData;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const metrics = t1;
  const t2 = card.slots.chart.series[0]?.label ?? "";
  let t3;
  if ($[2] !== t2 || $[3] !== translate) {
    t3 = translate(t2);
    $[2] = t2;
    $[3] = translate;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const placeholderLabel = t3;
  let t4;
  if ($[5] !== chart || $[6] !== selectedId) {
    t4 = selectChartSeries(chart, selectedId);
    $[5] = chart;
    $[6] = selectedId;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const selection = t4;
  const isEmpty = heroState === "no-connections" || heroState === "no-data";
  const canChooseMetric = heroState === "normal" && metrics.length > 1;
  let t5;
  if ($[8] !== selection?.metric.id) {
    t5 = () => {
      setDraftId(selection?.metric.id ?? "");
      setIsPickerOpen(true);
    };
    $[8] = selection?.metric.id;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const openPicker = t5;
  let t6;
  if ($[10] !== draftId) {
    t6 = apply => {
      if (apply && draftId) {
        setSelectedId(draftId);
      }
      setIsPickerOpen(false);
    };
    $[10] = draftId;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const closePicker = t6;
  let t7;
  if ($[12] !== canChooseMetric || $[13] !== metrics.length || $[14] !== openPicker) {
    t7 = metrics.length > 1 && <IconButton ariaLabel="Choose metric" title="Choose metric" onClick={openPicker} disabled={!canChooseMetric}><EditOutlined fontSize="small" /></IconButton>;
    $[12] = canChooseMetric;
    $[13] = metrics.length;
    $[14] = openPicker;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== heroState || $[17] !== placeholderLabel || $[18] !== selection) {
    t8 = heroState === "loading" || !selection && !placeholderLabel ? <Skeleton variant="text" width={112} /> : <Typography variant="heading-xl" component="h2" sx={theme_1 => ({
      color: heroState === "normal" ? theme_1.colors.text.primary : theme_1.colors.text.tertiary
    })}>{selection?.metric.label ?? placeholderLabel}</Typography>;
    $[16] = heroState;
    $[17] = placeholderLabel;
    $[18] = selection;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== t7 || $[21] !== t8) {
    t9 = <FlexRow sx={_temp4}>{t7}{t8}</FlexRow>;
    $[20] = t7;
    $[21] = t8;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== heroState) {
    t10 = heroState === "loading" && <Skeleton variant="rounded" width="100%" height={CHART_HEIGHT} />;
    $[23] = heroState;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== isEmpty) {
    t11 = isEmpty && <FlexColumn sx={_temp5}><PlaceholderLine /></FlexColumn>;
    $[25] = isEmpty;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== chart.xAxis || $[28] !== heroState || $[29] !== selection) {
    t12 = heroState === "normal" && selection && <LineChart key={selection.metric.id} series={selection.series} seriesData={selection.seriesData} xAxis={chart.xAxis} showLabels={true} height={CHART_HEIGHT} colorScheme="sand" animate={true} />;
    $[27] = chart.xAxis;
    $[28] = heroState;
    $[29] = selection;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  if ($[31] !== heroState || $[32] !== onConnect) {
    t13 = heroState === "no-connections" && <StateOverlay key="no-connections" title="Your trend appears here" body="Connect a platform and the graph lights up with real data" action={<Button variant="contained" size="small" onClick={onConnect}>Connect a platform</Button>} />;
    $[31] = heroState;
    $[32] = onConnect;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[34] !== heroState) {
    t14 = heroState === "no-data" && <StateOverlay key="no-data" title="Collecting data" body="Your trend lights up shortly" waiting={true} />;
    $[34] = heroState;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] !== t13 || $[37] !== t14) {
    t15 = <AnimatePresence>{t13}{t14}</AnimatePresence>;
    $[36] = t13;
    $[37] = t14;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  let t16;
  if ($[39] !== t10 || $[40] !== t11 || $[41] !== t12 || $[42] !== t15) {
    t16 = <ChartArea>{t10}{t11}{t12}{t15}</ChartArea>;
    $[39] = t10;
    $[40] = t11;
    $[41] = t12;
    $[42] = t15;
    $[43] = t16;
  } else {
    t16 = $[43];
  }
  let t17;
  if ($[44] !== closePicker || $[45] !== draftId || $[46] !== isPickerOpen || $[47] !== metrics) {
    t17 = <MetricPickerDialog open={isPickerOpen} metrics={metrics} draftId={draftId} onDraftChange={setDraftId} onClose={closePicker} />;
    $[44] = closePicker;
    $[45] = draftId;
    $[46] = isPickerOpen;
    $[47] = metrics;
    $[48] = t17;
  } else {
    t17 = $[48];
  }
  let t18;
  if ($[49] !== t16 || $[50] !== t17 || $[51] !== t9) {
    t18 = <FlexColumn component="section" sx={_temp3}>{t9}{t16}{t17}</FlexColumn>;
    $[49] = t16;
    $[50] = t17;
    $[51] = t9;
    $[52] = t18;
  } else {
    t18 = $[52];
  }
  return t18;
};
export default HeroLineChart;
function _temp(value) {
  return toNumber(value) !== null;
}
function _temp2(series) {
  return series?.data?.some(_temp);
}
function _temp3(theme) {
  return {
    gap: theme.sizes.spacing["07"]
  };
}
function _temp4(theme_0) {
  return {
    alignItems: "center",
    gap: theme_0.sizes.spacing["04"]
  };
}
function _temp5(theme_2) {
  return {
    color: theme_2.colors.border.tertiary
  };
}